import React, { useEffect, useRef, useState } from "react";

/**
 * Pull-to-refresh para o app instalado na tela inicial (PWA).
 *
 * No Android o gesto "arrastar para baixo para atualizar" já funciona
 * nativamente quando o app roda em modo standalone. No iOS, porém, o Safari
 * remove esse gesto quando o site é aberto pela tela inicial
 * (window.navigator.standalone === true). Este componente reintroduz o gesto
 * APENAS nesse cenário (iOS standalone), recarregando a página inteira.
 *
 * O gate em `window.navigator.standalone === true` é essencial: essa flag só
 * existe no iOS em modo standalone (é undefined no Android e no desktop),
 * então o comportamento não é duplicado onde o pull-to-refresh já é nativo.
 */

const THRESHOLD = 70; // distância (px) necessária para disparar o refresh
const MAX_PULL = 110; // deslocamento máximo do indicador
const RESISTANCE = 0.5; // "peso" do arraste

// Sobe na árvore procurando o container rolável mais próximo do toque.
// Se algum ancestral já estiver rolado (scrollTop > 0), não iniciamos o gesto,
// evitando conflito com a lista de mensagens (que pagina ao chegar no topo).
const findScrollableParent = (node) => {
  let el = node;
  while (el && el !== document.body && el !== document.documentElement) {
    if (el.scrollHeight > el.clientHeight) {
      const overflowY = window.getComputedStyle(el).overflowY;
      if (overflowY === "auto" || overflowY === "scroll") {
        return el;
      }
    }
    el = el.parentElement;
  }
  return null;
};

const PullToRefresh = () => {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const startYRef = useRef(0);
  const trackingRef = useRef(false);
  const pullRef = useRef(0);

  useEffect(() => {
    const isIosStandalone = window.navigator.standalone === true;
    if (!isIosStandalone) return undefined;

    const handleTouchStart = (e) => {
      if (refreshing || e.touches.length !== 1) return;

      const scrollable = findScrollableParent(e.target);
      // Só habilita o gesto quando não há conteúdo rolado acima
      const atTop = !scrollable || scrollable.scrollTop <= 0;
      if (!atTop) return;

      startYRef.current = e.touches[0].clientY;
      trackingRef.current = true;
    };

    const handleTouchMove = (e) => {
      if (!trackingRef.current || refreshing) return;

      const delta = e.touches[0].clientY - startYRef.current;
      if (delta <= 0) {
        pullRef.current = 0;
        setPull(0);
        return;
      }

      // impede a rubber-band interna enquanto o usuário puxa para atualizar
      if (e.cancelable) e.preventDefault();

      const distance = Math.min(delta * RESISTANCE, MAX_PULL);
      pullRef.current = distance;
      setPull(distance);
    };

    const handleTouchEnd = () => {
      if (!trackingRef.current) return;
      trackingRef.current = false;

      if (pullRef.current >= THRESHOLD) {
        setRefreshing(true);
        setPull(THRESHOLD);
        window.location.reload();
        return;
      }

      pullRef.current = 0;
      setPull(0);
    };

    document.addEventListener("touchstart", handleTouchStart, { passive: true });
    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", handleTouchEnd, { passive: true });
    document.addEventListener("touchcancel", handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
      document.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [refreshing]);

  if (pull <= 0 && !refreshing) return null;

  const progress = Math.min(pull / THRESHOLD, 1);
  const ready = pull >= THRESHOLD;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        display: "flex",
        justifyContent: "center",
        pointerEvents: "none",
        zIndex: 3000,
        transform: `translateY(${pull - 20}px)`,
        transition: trackingRef.current ? "none" : "transform 0.2s ease",
      }}
    >
      <div
        style={{
          width: 34,
          height: 34,
          borderRadius: "50%",
          background: "#ffffff",
          boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          opacity: Math.min(progress + 0.2, 1),
        }}
      >
        <div
          style={{
            width: 18,
            height: 18,
            border: "2px solid #d0d0d0",
            borderTopColor: "#2D7BE8",
            borderRadius: "50%",
            transform: `rotate(${pull * 4}deg)`,
            animation: refreshing ? "pullToRefreshSpin 0.7s linear infinite" : "none",
            opacity: ready || refreshing ? 1 : 0.7,
          }}
        />
      </div>
      <style>
        {`@keyframes pullToRefreshSpin { to { transform: rotate(360deg); } }`}
      </style>
    </div>
  );
};

export default PullToRefresh;
